import { ReactNode } from 'react'
import { cn } from '../lib/cn'

export interface StaticTableColumn<T> {
  key: string
  header: string
  align?: 'left' | 'center' | 'right'
  /** Se aplica a la celda y al encabezado; sirve para fijar el ancho. */
  className?: string
  render: (row: T) => ReactNode
}

const ALIGNMENTS = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
} as const

interface StaticTableProps<T> {
  columns: StaticTableColumn<T>[]
  rows: T[]
  getRowKey: (row: T, index: number) => string
  footer?: ReactNode
  className?: string
}

/**
 * Tabla estática. No usa `@tanstack/react-table` a propósito: un documento
 * impreso no tiene orden, filtro ni paginación de cliente, y esa librería
 * arrastraría estado de React a un árbol que debe ser 100% servidor.
 *
 * `table-header-group` en el `<thead>` hace que Chromium repita los encabezados
 * cuando la tabla cruza de página.
 */
export const StaticTable = <T,>({
  columns,
  rows,
  getRowKey,
  footer,
  className,
}: StaticTableProps<T>) => (
  <table className={cn('w-full border-collapse text-[9pt]', className)}>
    <thead className='table-header-group'>
      <tr className='border-b border-zinc-300'>
        {columns.map((column) => (
          <th
            key={column.key}
            className={cn(
              'py-1.5 text-[8pt] font-semibold uppercase tracking-wide text-zinc-500',
              ALIGNMENTS[column.align ?? 'left'],
              column.className,
            )}
          >
            {column.header}
          </th>
        ))}
      </tr>
    </thead>

    <tbody>
      {rows.map((row, index) => (
        <tr
          key={getRowKey(row, index)}
          className='break-inside-avoid border-b border-zinc-100'
        >
          {columns.map((column) => (
            <td
              key={column.key}
              className={cn(
                'py-1.5 align-top',
                ALIGNMENTS[column.align ?? 'left'],
                column.align && column.align !== 'left' && 'tabular-nums',
                column.className,
              )}
            >
              {column.render(row)}
            </td>
          ))}
        </tr>
      ))}
    </tbody>

    {footer && <tfoot className='break-inside-avoid'>{footer}</tfoot>}
  </table>
)
